import React from 'react';
import { ColumnDef, Row } from '@tanstack/react-table';
import { formatDistanceToNow } from 'date-fns';

import { Guest, GuestListItem, GuestStatus, isPlusOneGuestItem } from 'types/guest.types';
import { Badge } from '@epoch-ui/components/Badge';

const renderGuestStatusBadge = (status: GuestStatus): React.ReactNode => {
  switch (status) {
    case GuestStatus.CONFIRMED:
      return <Badge variant="positive">Going</Badge>;
    case GuestStatus.CONFIRMED_WITH_INCOMPLETE_SURVEY:
      return <Badge variant="warning">Incomplete</Badge>;
    case GuestStatus.INTERESTED:
      return <Badge variant="primary">Maybe</Badge>;
    case GuestStatus.WAITLISTED:
      return <Badge variant="purple">Waitlisted</Badge>;
    case GuestStatus.CANCELLED:
      return <Badge variant="destructive">Not going</Badge>;
    case GuestStatus.ACTION_REQUIRED:
      return <Badge variant="secondary">Pending</Badge>;
    case GuestStatus.INVITED:
      return <Badge variant="secondary">Invited</Badge>;
    case GuestStatus.ATTENDED:
      return <Badge variant="positive">Attended</Badge>;
    default:
      return <Badge variant="outline">-</Badge>;
  }
};

type GuestListColumnsFactoryProps = {
  onNameClick: (gid: string, plusOneId?: number | null) => void;
  shouldShowAttendanceColumn: boolean;
};

export const guestListColumnsFactory = ({
  onNameClick,
  shouldShowAttendanceColumn
}: GuestListColumnsFactoryProps): ColumnDef<GuestListItem>[] => [
  {
    accessorKey: 'firstName',
    header: 'Name',
    cell: ({ row }): React.ReactNode => {
      const item = row.original;
      if (isPlusOneGuestItem(item)) {
        const ownerName = `${item.owner.firstName} ${item.owner.lastName}`;
        const fullName = `${item.firstName} ${item.lastName} (${ownerName})`;
        return (
          <div
            role="button"
            tabIndex={0}
            onClick={() => onNameClick(item.owner.gid, item.plusOneId)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                onNameClick(item.owner.gid, item.plusOneId);
              }
            }}
            className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-font-medium"
          >
            <Badge variant="secondary" size="sm" className="tw-rounded-md">
              +1
            </Badge>
            {fullName}
          </div>
        );
      }
      const guest = item as Guest;
      const firstName = guest.firstName || '';
      const lastName = guest.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim();
      return (
        <div
          role="button"
          tabIndex={0}
          onClick={() => onNameClick(guest.gid)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onNameClick(guest.gid);
            }
          }}
          className="tw-cursor-pointer tw-font-medium"
        >
          {fullName || '-'}
        </div>
      );
    }
  },
  {
    accessorKey: 'department',
    header: 'Department',
    cell: ({ row }): React.ReactNode => {
      if (isPlusOneGuestItem(row.original)) {
        return <div>-</div>;
      }
      return <div>{(row.original as Guest).department || '-'}</div>;
    }
  },
  {
    accessorKey: 'status',
    header: 'RSVP',
    cell: ({ row }): React.ReactNode => {
      return renderGuestStatusBadge(row.original.status);
    }
  },

  ...(shouldShowAttendanceColumn
    ? [
        {
          accessorKey: 'attendance',
          header: 'Attendance',
          cell: ({ row }: { row: Row<GuestListItem> }): React.ReactNode => {
            if (isPlusOneGuestItem(row.original)) {
              return <div>{row.original.isCheckedIn ? 'Attended' : '-'}</div>;
            }
            return <div>{(row.original as Guest).isAttended ? 'Attended' : '-'}</div>;
          }
        }
      ]
    : []),

  {
    accessorKey: 'lastUpdatedAt',
    header: 'Last updated',
    cell: ({ row }): React.ReactNode => {
      const { lastUpdatedAt } = row.original;
      if (!lastUpdatedAt) {
        return <div>-</div>;
      }
      try {
        const formattedTime = formatDistanceToNow(new Date(lastUpdatedAt), {
          addSuffix: true
        });
        return <div>{formattedTime}</div>;
      } catch {
        return <div>-</div>;
      }
    }
  }
];

export const formatFilterStatuses = (state: string): string => {
  if (state.toLowerCase() === 'pending') return 'action_required';
  if (state.toLowerCase() === 'incomplete') return 'confirmed_with_incomplete_survey';
  if (state.toLowerCase() === 'maybe') return 'interested';
  if (state.toLowerCase() === 'going') return 'confirmed';
  if (state.toLowerCase() === 'not going') return 'cancelled';
  return state.toLowerCase();
};

export const formatDepartmentsFilter = (state: string): string => {
  if (state === '-') return 'none';
  return state;
};
